import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { AppHeader } from "../components/AppHeader";
import { Input } from "../components/ui/input";
import { PasswordInput } from "../components/ui/password-input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { ArrowLeft, User as UserIcon, Lock, Save, LogOut, BadgeCheck, Copy } from "lucide-react";
import { toast } from "sonner";

export default function Settings() {
  const navigate = useNavigate();
  const { user, logout, refresh } = useAuth();
  const [profile, setProfile] = useState({ name: "", business_name: "" });
  const [savingProfile, setSavingProfile] = useState(false);
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [savingPw, setSavingPw] = useState(false);
  const [code, setCode] = useState("");

  useEffect(() => {
    if (user) setProfile({ name: user.name || "", business_name: user.business_name || "" });
    apiClient.get("/me/supplier-code").then((r) => setCode(r.data.code)).catch(() => {});
  }, [user]);

  const setP = (k) => (e) => setProfile((f) => ({ ...f, [k]: e.target.value }));
  const setPW = (k) => (e) => setPw((f) => ({ ...f, [k]: e.target.value }));

  const saveProfile = async (e) => {
    e.preventDefault();
    if (!profile.name.trim()) { toast.error("Name is required"); return; }
    setSavingProfile(true);
    try {
      await apiClient.put("/auth/me", {
        name: profile.name.trim(),
        business_name: profile.business_name.trim(),
      });
      await refresh();
      toast.success("Profile updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally { setSavingProfile(false); }
  };

  const savePw = async (e) => {
    e.preventDefault();
    if (pw.new_password.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    if (pw.new_password !== pw.confirm) { toast.error("New passwords do not match"); return; }
    setSavingPw(true);
    try {
      await apiClient.post("/auth/change-password", {
        current_password: pw.current_password,
        new_password: pw.new_password,
      });
      setPw({ current_password: "", new_password: "", confirm: "" });
      toast.success("Password changed");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally { setSavingPw(false); }
  };

  const copyCode = async () => {
    try { await navigator.clipboard.writeText(code); toast.success("Code copied"); }
    catch { toast.error("Copy failed"); }
  };

  return (
    <>
      <AppHeader title="Settings" subtitle="Profile & security" />
      <div className="px-4 pt-4 pb-28 space-y-4">
        <button onClick={() => navigate(-1)} data-testid="settings-back" className="flex items-center gap-1 text-sm font-semibold text-slate-600">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        {/* Account identity */}
        <div className="bg-white rounded-2xl border border-slate-200 p-4 space-y-3" data-testid="account-identity">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-emerald-600 text-white grid place-items-center shrink-0 font-bold text-lg">
              {(user?.name || "?").charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="font-bold text-slate-900 truncate">{user?.name}</div>
              <div className="text-xs text-slate-500 truncate">{user?.email}</div>
            </div>
          </div>
          {code && (
            <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
              <BadgeCheck className="w-4 h-4 text-emerald-700" />
              <span className="text-xs text-slate-500">Supplier code</span>
              <code data-testid="settings-supplier-code" className="text-sm font-bold tracking-widest text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-2 py-0.5">
                {code}
              </code>
              <Button size="icon" variant="ghost" onClick={copyCode} data-testid="settings-copy-code" className="h-7 w-7 ml-auto">
                <Copy className="w-3.5 h-3.5" />
              </Button>
            </div>
          )}
        </div>

        {/* Profile edit */}
        <form onSubmit={saveProfile} data-testid="settings-profile-form" className="bg-white rounded-2xl border border-slate-200 p-4 space-y-3">
          <div className="flex items-center gap-2">
            <UserIcon className="w-4 h-4 text-slate-500" />
            <h3 className="text-sm font-bold uppercase text-slate-500 tracking-wider">Profile</h3>
          </div>
          <div className="space-y-1.5">
            <Label>Your name</Label>
            <Input data-testid="settings-input-name" value={profile.name} onChange={setP("name")} className="h-12 rounded-xl text-base" required />
          </div>
          <div className="space-y-1.5">
            <Label>Business / shop name</Label>
            <Input data-testid="settings-input-business" value={profile.business_name} onChange={setP("business_name")} placeholder="e.g., Ramesh General Store" className="h-12 rounded-xl text-base" />
          </div>
          <Button type="submit" disabled={savingProfile} data-testid="settings-save-profile" className="w-full h-12 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold gap-2">
            <Save className="w-4 h-4" /> {savingProfile ? "Saving..." : "Save profile"}
          </Button>
        </form>

        {/* Password change */}
        <form onSubmit={savePw} data-testid="settings-pw-form" className="bg-white rounded-2xl border border-slate-200 p-4 space-y-3">
          <div className="flex items-center gap-2">
            <Lock className="w-4 h-4 text-slate-500" />
            <h3 className="text-sm font-bold uppercase text-slate-500 tracking-wider">Change password</h3>
          </div>
          <div className="space-y-1.5">
            <Label>Current password</Label>
            <PasswordInput data-testid="settings-pw-current" value={pw.current_password} onChange={setPW("current_password")} className="h-12 rounded-xl text-base" required />
          </div>
          <div className="space-y-1.5">
            <Label>New password</Label>
            <PasswordInput data-testid="settings-pw-new" value={pw.new_password} onChange={setPW("new_password")} minLength={6} className="h-12 rounded-xl text-base" required />
          </div>
          <div className="space-y-1.5">
            <Label>Confirm new password</Label>
            <PasswordInput data-testid="settings-pw-confirm" value={pw.confirm} onChange={setPW("confirm")} minLength={6} className="h-12 rounded-xl text-base" required />
          </div>
          <Button type="submit" disabled={savingPw} data-testid="settings-save-pw" className="w-full h-12 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold gap-2">
            <Save className="w-4 h-4" /> {savingPw ? "Updating..." : "Update password"}
          </Button>
        </form>

        {/* Logout */}
        <Button
          onClick={logout}
          variant="ghost"
          data-testid="settings-logout"
          className="w-full h-12 rounded-xl text-red-600 hover:text-red-700 hover:bg-red-50 font-semibold gap-2"
        >
          <LogOut className="w-4 h-4" /> Log out
        </Button>
      </div>
    </>
  );
}
